import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, RefreshCw, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import AdminTabs from '../AdminTabs';
import { useIsMobile } from '../../../hooks/use-mobile';
import {
  AdminPage, AdminHead, Banner, Button, EmptyRow, SearchField, SkeletonRows, StatTiles, StatusPill, relativeTime,
} from '../kit';
import {
  apiErrorMessage, fetchOrganizationsSummary, startFactRefresh,
} from '../../../utils/organizationApi';
import { ordinal, orgInitial } from './orgModel';
import { buildRunRequest, computeNextRun, deriveRefreshState } from './refreshModel';
import useRefreshTracker, { announceOutcome } from './useRefreshTracker';
import AddOrganizationPanel from './AddOrganizationPanel';
import OrgCardList from './OrgCardList';
import './orgs.css';

const COLUMNS = 8;

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);
  const [adding, setAdding] = useState(false);
  const [starting, setStarting] = useState({});
  const [finished, setFinished] = useState({}); // orgId → true once a tracked run ends (until reload)
  const announce = useRef(new Set());
  const namesRef = useRef({});
  const adoptedRef = useRef(new Set());

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchOrganizationsSummary();
      setData(res);
      setError(null);
      setFinished({});
    } catch (err) {
      if (!silent) setError(apiErrorMessage(err, 'Could not load organizations.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFinish = useCallback((orgId, status) => {
    setFinished((prev) => ({ ...prev, [orgId]: true }));
    if (announce.current.has(orgId)) {
      announce.current.delete(orgId);
      announceOutcome(namesRef.current[orgId], status);
    }
    load({ silent: true });
  }, [load]);

  const { statuses, track } = useRefreshTracker(onFinish);
  const orgs = useMemo(() => data?.organizations || [], [data]);

  // Adopt runs already in flight (a scheduled run, or one started elsewhere)
  useEffect(() => {
    orgs.forEach((o) => {
      namesRef.current[o.id] = o.name;
      if (o.inFlight?.running && !adoptedRef.current.has(o.id)) {
        adoptedRef.current.add(o.id);
        track(o.id, o.inFlight);
      }
    });
  }, [orgs, track]);

  const rows = useMemo(() => orgs.map((o) => {
    const liveStatus = statuses[o.id] || (finished[o.id] ? { running: false } : null);
    return { org: o, state: deriveRefreshState(o, { now: new Date(), liveStatus }), next: computeNextRun(o) };
  }), [orgs, statuses, finished]);

  const failedRows = rows.filter((r) => r.state.key === 'failed' || r.state.key === 'partial');
  const runningRows = rows.filter((r) => r.state.key === 'running');
  const refreshedToday = rows.filter((r) => r.state.refreshedToday).length;
  const totalLocations = orgs.reduce((n, o) => n + (Number(o.locationCount) || 0), 0);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(({ org, state }) => {
      if (failedOnly && !(state.key === 'failed' || state.key === 'partial')) return false;
      if (!q) return true;
      return [org.name, org.code, String(org.id)].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, query, failedOnly]);

  const runRefresh = async (org) => {
    setStarting((prev) => ({ ...prev, [org.id]: true }));
    try {
      await startFactRefresh(buildRunRequest(org, { mode: 'default', locationIds: [] }));
      announce.current.add(org.id);
      setFinished((prev) => { const next = { ...prev }; delete next[org.id]; return next; });
      track(org.id, { running: true, startedAt: new Date().toISOString(), elapsedMs: 0, tablesDone: 0, tablesTotal: 4 });
      toast.success(`Refresh started — ${org.name}. It runs in the background.`);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast('A refresh is already running for this organization.');
        track(org.id);
      } else {
        toast.error(apiErrorMessage(err, 'Could not start the refresh.'));
      }
    } finally {
      setStarting((prev) => { const next = { ...prev }; delete next[org.id]; return next; });
    }
  };

  const firstFailed = failedRows[0];
  const firstRunning = runningRows[0];
  const loadingStats = loading && !data;
  const stats = [
    {
      key: 'orgs',
      label: 'Organizations',
      value: loadingStats ? '—' : orgs.length,
      sub: loadingStats ? ' ' : `${totalLocations} location${totalLocations === 1 ? '' : 's'}`,
    },
    {
      key: 'today',
      label: 'Refreshed today',
      value: loadingStats ? '—' : `${refreshedToday} / ${orgs.length}`,
      sub: 'Successful run since midnight, org time',
    },
    {
      key: 'failed',
      label: 'Failed refreshes',
      value: loadingStats ? '—' : failedRows.length,
      tone: failedRows.length ? 'bad' : undefined,
      sub: firstFailed
        ? `${firstFailed.org.name}${failedRows.length > 1 ? ` + ${failedRows.length - 1} more` : ''} · ${relativeTime(firstFailed.state.failedAt) || 'recently'}`
        : 'None',
      onClick: failedRows.length ? () => setFailedOnly(true) : undefined,
    },
    {
      key: 'running',
      label: 'Running now',
      value: loadingStats ? '—' : runningRows.length,
      sub: firstRunning
        ? `${firstRunning.org.name} · ${firstRunning.state.running.tablesDone ?? 0} of ${firstRunning.state.running.tablesTotal || 4} tables`
        : 'Nothing running',
    },
  ];

  const openOrg = (id) => navigate(`/admin/organizations/${id}`);

  // Phones: the same empty states as the table, as card-list notes.
  let phoneEmpty = null;
  if (!loading && !error && orgs.length === 0) {
    phoneEmpty = {
      title: 'No organizations yet',
      body: 'Add the first organization to start loading its properties.',
      action: <Button variant="primary" icon={Plus} onClick={() => setAdding(true)}>Add organization</Button>,
    };
  } else if (!loading && orgs.length > 0 && visible.length === 0) {
    phoneEmpty = {
      title: failedOnly && !query ? 'No failed refreshes' : 'No matches',
      body: query ? `No organization matches “${query}”.` : 'Every organization refreshed without errors.',
      action: <Button onClick={() => { setQuery(''); setFailedOnly(false); }}>Show all</Button>,
    };
  }

  return (
    <AdminPage>
      <AdminTabs />
      <AdminHead
        title="Organizations"
        subtitle="Organizations, their properties, and the nightly data refresh, all in one place."
        actions={<Button variant="primary" icon={Plus} onClick={() => setAdding(true)}>Add organization</Button>}
      />

      {error ? (
        <Banner
          tone="bad"
          icon={AlertTriangle}
          title="Couldn't load organizations"
          action={<Button onClick={() => load()}>Try again</Button>}
        >
          {error}
        </Banner>
      ) : null}
      {data?.warnings?.length ? (
        <Banner tone="warn" icon={AlertTriangle} title="Some details are missing">{data.warnings.join(' ')}</Banner>
      ) : null}

      <StatTiles stats={stats} />

      <div className="ak-frame">
        <div className={`ak-toolbar${isMobile ? ' ak-m-toolbar' : ''}`}>
          <SearchField value={query} onChange={setQuery} placeholder="Search name, code or ID" label="Search organizations" />
          {failedOnly ? (
            <button type="button" className="ak-chip ak-active orgs-filter-chip" onClick={() => setFailedOnly(false)} aria-label="Clear failed refreshes filter">
              Failed refreshes <X aria-hidden="true" />
            </button>
          ) : null}
          <span className="ak-toolbar-meta ak-hide-sm">Select a row to open the organization</span>
        </div>
        {isMobile ? (
          <OrgCardList rows={visible} loading={loading && !data} empty={phoneEmpty} onOpen={openOrg} />
        ) : (
          <div className="ak-scroll">
            <table className="ak-tbl orgs-tbl">
              <thead>
                <tr>
                  <th scope="col">Organization</th>
                  <th scope="col">Locations</th>
                  <th scope="col">Users</th>
                  <th scope="col">Currency</th>
                  <th scope="col">Close day</th>
                  <th scope="col">Data refresh</th>
                  <th scope="col">Next run</th>
                  <th scope="col" className="orgs-w-refresh"><span className="ak-sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading && !data ? <SkeletonRows rows={6} columns={COLUMNS} /> : null}
                {!loading && !error && orgs.length === 0 ? (
                  <EmptyRow
                    colSpan={COLUMNS}
                    title="No organizations yet"
                    body="Add the first organization to start loading its properties."
                    action={<Button variant="primary" icon={Plus} onClick={() => setAdding(true)}>Add organization</Button>}
                  />
                ) : null}
                {!loading && orgs.length > 0 && visible.length === 0 ? (
                  <EmptyRow
                    colSpan={COLUMNS}
                    title={failedOnly && !query ? 'No failed refreshes' : 'No matches'}
                    body={query ? `No organization matches “${query}”.` : 'Every organization refreshed without errors.'}
                    action={<Button onClick={() => { setQuery(''); setFailedOnly(false); }}>Show all</Button>}
                  />
                ) : null}
                {visible.map(({ org, state, next }) => {
                  const running = state.key === 'running';
                  const busy = running || starting[org.id];
                  return (
                    <tr
                      key={org.id}
                      className="ak-click"
                      tabIndex={0}
                      onClick={() => openOrg(org.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) openOrg(org.id); }}
                      aria-label={`Open ${org.name}`}
                    >
                      <td>
                        <div className="ak-cell-id">
                          <div className="ak-orgmark ak-sm" aria-hidden="true">{orgInitial(org)}</div>
                          <div>
                            <Link className="orgs-name" to={`/admin/organizations/${org.id}`} onClick={(e) => e.stopPropagation()}>
                              {org.name || 'Unnamed organization'}
                            </Link>
                            <span className="ak-mono ak-muted">{org.code || '—'} · #{org.id}</span>
                          </div>
                        </div>
                      </td>
                      <td>{org.locationCount ?? '—'}</td>
                      <td>{org.userCount ?? '—'}</td>
                      <td className="ak-mono">{org.reportingCurrency || 'USD'}</td>
                      <td>{ordinal(org.financialCloseDay ?? 15)}</td>
                      <td>
                        <div className="orgs-status">
                          <StatusPill tone={state.tone} running={running}>{state.label}</StatusPill>
                          <span className="orgs-sub" title={state.errorText || state.detail}>{state.detail}</span>
                        </div>
                      </td>
                      <td className="orgs-muted-cell">{next.label}</td>
                      <td className="orgs-w-refresh">
                        <Button
                          size="small"
                          icon={RefreshCw}
                          className={busy ? 'orgs-busy' : undefined}
                          disabled={busy}
                          onClick={(e) => { e.stopPropagation(); runRefresh(org); }}
                          aria-label={running ? `${org.name} is refreshing` : `Refresh ${org.name} now`}
                          title={running ? 'A refresh is running' : `Run the schedule's default window now (${org.dashboardRefreshDaysBack ?? 30} days back, ${org.dashboardRefreshDaysForward ?? 365} forward)`}
                        >
                          {running ? 'Running…' : starting[org.id] ? 'Starting…' : 'Refresh'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddOrganizationPanel
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={(org) => { setAdding(false); navigate(`/admin/organizations/${org.id}`); }}
      />
    </AdminPage>
  );
}
